import { Injectable } from '@angular/core';
import { AlertController, Platform } from "@ionic/angular";
import { FileService } from '../file/file.service';
import { AnalyticsService, UtilityService } from "src/app/shared/services";
import { EventDataService } from 'src/app/shared/services/eventData/event-data.service';
import { TranslateService } from '@ngx-translate/core';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';

@Injectable({
    providedIn: 'root'
})
export class DocumentService {
    private loader: HTMLIonLoadingElement;

    constructor(
        private fileService: FileService,
        private platform: Platform,
        private SAnalytics: AnalyticsService,
        private SUtility: UtilityService,
        private SEventData: EventDataService,
        private alertCtrl: AlertController,
        private translateService: TranslateService
    ) { }

    /**
     * @param document 
     * @param eventId
     * @description Open the document provided as "document" param and on 
     * mobile devices it creates a directory (EzbizEventDoc) and download the 
     * documents before open them 
     */
    public openDocument(document: any, eventId: string) {
        this.SAnalytics.documentAccess(eventId, document);
        const isNativeApp = Capacitor.isNativePlatform();
        
        if (isNativeApp) {
            if (this.fileService.fileFormats.includes(document.type)) {
                this.openDocMobile(document);
            } else {
                this.openDocWeb(document);
            }
        } else {
            this.openDocWeb(document);
        }
    }

    /**
     * Forces download of a document instead of opening it in a new tab.
     * On mobile: downloads via Capacitor Filesystem.
     * On web: fetches as blob and triggers browser download.
     */
    public async downloadDocument(document: any, eventId: string): Promise<void> {
        this.SAnalytics.documentAccess(eventId, document);
        const isNativeApp = Capacitor.isNativePlatform();
        const language = this.SEventData.getLanguage();
        const fileName = document.name?.[language] || 'document';
        const ext = document.type || '';

        if (isNativeApp) {
            if (this.fileService.fileFormats.includes(ext)) {
                await this.downloadDocMobile(document, fileName, ext);
            } else {
                await this.forceDownloadWeb(document.url, fileName, ext);
            }
        } else {
            await this.forceDownloadWeb(document.url, fileName, ext);
        }
    }

    private async forceDownloadWeb(url: string, fileName: string, ext: string): Promise<void> {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = fileName.includes('.') ? fileName : `${fileName}.${ext}`;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Download failed, falling back to window.open', error);
            window.open(url, '_blank');
        }
    }

    private async downloadDocMobile(document: any, fileName: string, ext: string): Promise<void> {
        this.presentLoading();
        try {
            const response = await this.fileService.downloadFile(fileName, ext, document.url);
            await this.fileService.openFile(response.path, ext)
                .catch(() => this.docOpeningErr(`file://${response.path}`));
        } catch (error) {
            console.error(error);
            this.genericErr();
        } finally {
            this.closeLoading();
        }
    }

    /**
     * Download a document to the "Documents" directory and open it if possible
     * i.e. so far exel files are not possible to open
     */
    private openDocMobile(document: any) {
        this.presentLoading();
        const language = this.SEventData.getLanguage();

        this.fileService.downloadFile(
            document.name[language], 
            document.type, 
            document.url
        ).then((response) => {
            this.fileService.openFile(response.path, document.type)
                .catch(() => this.docOpeningErr(`file://${response.path}`))
                .finally(() => this.closeLoading())
        }).catch((error) => {
            console.error(error);
            this.genericErr()
            this.closeLoading();
        });
    }

    /**
     * Open docuemnt in a new tab of a web browser
     * @param document 
     */
    private openDocWeb(document: any): void {
        window.open(document.url, "_blank");
    }

    private async docOpeningErr(uri: string) {
        const alert = await this.alertCtrl.create({
            header: this.translateService.instant("global.alerts.error"),
            message: this.translateService.instant("global.alerts.no_app_found_to_open_doc"),
            buttons: [
                {
                    text: this.translateService.instant("global.buttons.share"),
                    handler: () => { 
                        Share.share({ url: uri }).catch(() => this.genericErr())
                    }
                },
                { text: this.translateService.instant("global.buttons.back") }
            ]
        });
        alert.present();
    }

    private async genericErr() {
        const alert = await this.alertCtrl.create({
            header: this.translateService.instant("global.alerts.error"),
            message: this.translateService.instant("global.alerts.generic_error"),
            buttons: [{
                text: this.translateService.instant("global.buttons.back")
            }]
        });
        alert.present();
    }

    /**
     * Present loading
     */
    private async presentLoading() {
        this.loader = await this.SUtility.presentLoading("Loading...", 0, "crescent");
        await this.loader.present();
    }

    /**
     * Close loading
     */
    private async closeLoading() {
        await this.loader.dismiss();
    }
}
